import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import useApi from '@/hooks/useApi';
import useAuth from '@/components/UserContext';
import useApp from '@/pages/App.context';
import MethodSelection, { type KycMethod } from './MethodSelection';
import StartNewVerification from './StartNewVerification';
import ConnectingPanel from './ConnectingPanel';
import SuccessBanner from '@/components/SuccessBanner';
import VerifiedCard from './VerifiedCard';
import TrustAside from './TrustAside';
import WhyItMatters from './WhyItMatters';
import WhatThisUnlocks from './WhatThisUnlocks';
import {
  WEBID_CODE,
  rememberProvider,
  resolveProviderName,
} from './lastProvider.util';
import { type WebidStartRequest } from './webidForm.types';
import styles from './Kyc.module.scss';

type View = 'method-selection' | 'new-verification' | 'connecting';

type LocationState = {
  justVerified?: boolean;
};

const KycProvider = () => {
  const { hasKYC, userData, organizationData } = useAuth();
  const { t } = useTranslation(['common', 'onboarding']);
  const { setAppTitle, setHeadline } = useApp();
  const navigate = useNavigate();
  const location = useLocation();

  const locationState = (location.state ?? {}) as LocationState;
  const [showSuccessBanner, setShowSuccessBanner] = useState(
    !!locationState.justVerified
  );
  const [view, setView] = useState<View>('method-selection');
  const [method, setMethod] = useState<KycMethod | null>(null);
  const [selectedProvider, setSelectedProvider] = useState('none');
  const [providerError, setProviderError] = useState<string | undefined>();

  const [initializeKyc, { loading: initLoading }] = useApi('kyc.init');
  const [triggerWebid, { loading: webidLoading }] = useApi('webid.init');

  useEffect(() => {
    setAppTitle(t('common:menu.kycVerification'));
  }, [setAppTitle, t]);

  useEffect(() => {
    setHeadline({
      title: hasKYC
        ? t('onboarding:kycVerified.headline.title')
        : t('onboarding:kycSelection.headline.title'),
      subtitle: hasKYC
        ? t('onboarding:kycVerified.headline.subtitle')
        : t('onboarding:kycSelection.headline.subtitle'),
    });
  }, [setHeadline, hasKYC, t]);

  const handleMethodChange = (next: KycMethod) => {
    setMethod(next);
    setProviderError(undefined);
  };

  const handleProviderChange = (next: string) => {
    setSelectedProvider(next);
    setProviderError(undefined);
  };

  const handleMethodSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // "Create new" routes to the WebID prep step rather than redirecting out.
    if (method === 'new') {
      setView('new-verification');
      return;
    }

    if (method !== 'existing' || selectedProvider === 'none' || initLoading) {
      return;
    }

    setView('connecting');
    setProviderError(undefined);
    rememberProvider(selectedProvider);

    const result = await initializeKyc({ provider: selectedProvider });

    if (result?.response?.url) {
      window.location.href = result.response.url;
      return;
    }

    setProviderError(t('onboarding:kycSelection.providerError'));
    setView('method-selection');
  };

  const handleStartNewVerification = async (data: WebidStartRequest) => {
    if (webidLoading) {
      return;
    }

    setView('connecting');
    rememberProvider(WEBID_CODE);

    const result = await triggerWebid(data);

    if (result?.response?.url) {
      window.location.href = result.response.url;
      return;
    }

    setProviderError(t('onboarding:kycSelection.providerError'));
    setView('new-verification');
  };

  const handleBackToProfile = () => {
    void navigate('/account');
  };

  if (hasKYC) {
    return (
      <div className={styles.layout}>
        <div className={styles.main}>
          {showSuccessBanner && (
            <SuccessBanner
              title={t('onboarding:kycVerified.banner.title')}
              subtitle={t('onboarding:kycVerified.banner.subtitle')}
              dismissLabel={t('onboarding:kycVerified.banner.dismiss')}
              onDismiss={() => setShowSuccessBanner(false)}
            />
          )}
          <VerifiedCard onBackToProfile={handleBackToProfile} />
        </div>
        <aside className={styles.aside}>
          <WhatThisUnlocks />
        </aside>
      </div>
    );
  }

  if (view === 'connecting') {
    const code = method === 'new' ? WEBID_CODE : selectedProvider;
    const providerName = resolveProviderName(
      code,
      t('onboarding:kycVerified.card.fallbackProvider')
    );

    return (
      <div className={styles.layout}>
        <div className={styles.main}>
          <ConnectingPanel
            providerName={providerName}
            onCancel={() => setView('method-selection')}
          />
        </div>
      </div>
    );
  }

  if (view === 'new-verification') {
    return (
      <div className={styles.layout}>
        <div className={styles.main}>
          <StartNewVerification
            loading={webidLoading === true}
            initialPhone={userData.phone?.formattedNumber ?? ''}
            initialCountry={organizationData?.country ?? ''}
            onBack={() => setView('method-selection')}
            onSubmit={(data) => void handleStartNewVerification(data)}
          />
        </div>
        <aside className={styles.aside}>
          <WhyItMatters />
        </aside>
      </div>
    );
  }

  return (
    <div className={styles.layout}>
      <div className={styles.main}>
        <MethodSelection
          method={method}
          selectedProvider={selectedProvider}
          loading={initLoading === true}
          errorMessage={providerError}
          onMethodChange={handleMethodChange}
          onProviderChange={handleProviderChange}
          onSubmit={(event) => void handleMethodSubmit(event)}
        />
      </div>
      <aside className={styles.aside}>
        <TrustAside />
      </aside>
    </div>
  );
};

export default KycProvider;
